import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { HeartPulse, CheckCircle2, XCircle, MinusCircle, Clock, ShieldAlert, ArrowLeft, Plus, RefreshCw, ExternalLink } from 'lucide-react';
import {
  fitnessApi,
  toCiFailure,
  describeFailures,
  type FitnessReport,
  type CheckResult,
  type CiFailure,
} from '@/services/fitnessApi';
import { useCapabilities } from '@/hooks/useCapabilities';

const STATUS_ICON = {
  passed: <CheckCircle2 className="h-4 w-4 text-emerald-500" />,
  failed: <XCircle className="h-4 w-4 text-red-500" />,
  skipped: <MinusCircle className="h-4 w-4 text-slate-400" />,
};

export default function FitnessPage() {
  const navigate = useNavigate();
  const { isDisabled } = useCapabilities();
  const [reports, setReports] = useState<FitnessReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fitnessApi
      .getResults()
      .then((data) => setReports(data.reports))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const failures = useMemo<CiFailure[]>(
    () =>
      reports.flatMap((r) =>
        r.checks.filter((c) => c.status === 'failed').map((c) => toCiFailure(r.repo, c))
      ),
    [reports]
  );

  /** Hand the real failure to the Insights loop — check name, output, commit
   * and the guardrail it enforces — instead of dropping the user on a blank
   * form. */
  function createGuardrailFrom(selected: CiFailure[]) {
    navigate('/insights', {
      state: { failures: selected, issueText: describeFailures(selected) },
    });
  }

  if (loading) {
    return <div className="flex h-64 items-center justify-center text-sm text-slate-500">Loading fitness data...</div>;
  }

  const allChecks = [...new Set(reports.flatMap((r) => r.checks.map((c) => c.name)))].sort();
  const totalChecks = reports.reduce((sum, r) => sum + r.summary.total, 0);
  const totalPassed = reports.reduce((sum, r) => sum + r.summary.passed, 0);
  const totalFailed = failures.length;

  // The guardrail governing a check, taken from the check row the API resolved.
  const governing = new Map<string, { id: string; title: string }>();
  for (const r of reports) {
    for (const c of r.checks) {
      if (c.guardrailId) governing.set(c.name, { id: c.guardrailId, title: c.guardrailTitle });
    }
  }

  return (
    <div className="p-8">
      <Link to="/verify" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to Verify
      </Link>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Architecture Fitness</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Compliance status of architecture fitness checks across repositories.
          </p>
        </div>
        {reports.length > 0 && (
          <div className="text-right">
            <div className="text-3xl font-bold text-slate-900 dark:text-slate-100">{totalPassed}/{totalChecks}</div>
            <div className="text-sm text-slate-500">checks passing</div>
            {totalFailed > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300 mt-1">
                {totalFailed} failing
              </span>
            )}
          </div>
        )}
      </div>

      {/* Insights loop: carry the real failures into the analysis */}
      {totalFailed > 0 && (
        <div className="mb-6 p-4 rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-900/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-purple-600" />
              <span className="text-sm font-medium text-purple-900 dark:text-purple-100">
                Insights: {totalFailed} failure{totalFailed > 1 ? 's' : ''} detected
              </span>
            </div>
            <button
              onClick={() => createGuardrailFrom(failures)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-purple-600 text-white text-xs font-medium hover:bg-purple-700"
            >
              <Plus className="w-3.5 h-3.5" />
              Create Check from Failures
            </button>
          </div>
          <p className="mt-2 text-xs text-purple-700 dark:text-purple-300">
            The failing check names, their output and the offending commits are carried into the analysis — you do not have to retype them.
          </p>
        </div>
      )}

      {isDisabled('fitness') && reports.length === 0 ? (
        // Deliberate opt-out (guided-repo-setup Phase D): "we don't run
        // fitness tests" is a decision, not a gap — no setup nudge, just the
        // reversible fact. (Existing reports still render below: content
        // stays visible, only the asking stops.)
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8 text-center">
          <HeartPulse className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-700" />
          <h2 className="mt-4 font-semibold text-slate-900 dark:text-slate-100">
            Fitness tests are disabled for this workspace
          </h2>
          <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
            This workspace opted out of architecture fitness tests, so SystemDox won’t ask
            about them.{' '}
            <Link to="/settings/repo-roles" className="text-blue-600 hover:underline dark:text-blue-400">
              Enable in Settings
            </Link>{' '}
            any time to publish checks and see compliance here.
          </p>
        </div>
      ) : error || reports.length === 0 ? (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8 text-center">
          <HeartPulse className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-700" />
          <h2 className="mt-4 font-semibold text-slate-900 dark:text-slate-100">
            {error ? 'Could not load fitness data' : 'No fitness data published yet'}
          </h2>
          <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
            Results arrive from GitHub as your CI runs the fitness tests SystemDox published.
            Connect the repository that holds them, then publish a guardrail — the first check run will appear here.
          </p>
          <Link
            to="/settings/repo-roles"
            className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
          >
            Configure Fitness Tests Repository
          </Link>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                <th className="text-left p-3 font-medium text-slate-500 sticky left-0 bg-slate-50 dark:bg-slate-800/50">Check</th>
                {reports.map((r) => (
                  <th key={r.repo} className="text-center p-3 font-medium text-slate-500 min-w-[120px]">
                    <div>{r.repo}</div>
                    <div className="text-[10px] font-normal mt-0.5 flex items-center justify-center gap-1">
                      <Clock className="h-2.5 w-2.5" />
                      {r.timestamp ? new Date(r.timestamp).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—'}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allChecks.map((checkName) => {
                const guardrail = governing.get(checkName);
                return (
                  <tr key={checkName} className="border-b border-slate-100 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/30">
                    <td className="p-3 sticky left-0 bg-white dark:bg-slate-900">
                      <div className="font-mono text-xs text-slate-900 dark:text-slate-100">
                        {checkName.replace('check_', '').replace(/_/g, ' ')}
                      </div>
                      {guardrail && (
                        <div className="flex items-center gap-1.5 mt-1">
                          <ShieldAlert className="h-3 w-3 text-purple-500 shrink-0" />
                          <Link
                            to={`/docs/guardrails/${guardrail.id}`}
                            className="text-[10px] text-purple-600 hover:underline truncate"
                            title={`Why this check exists: ${guardrail.title}`}
                          >
                            {guardrail.title}
                          </Link>
                        </div>
                      )}
                    </td>
                    {reports.map((r) => {
                      const check: CheckResult | undefined = r.checks.find((c) => c.name === checkName);
                      const failed = check?.status === 'failed';
                      return (
                        <td key={r.repo} className="p-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {check ? STATUS_ICON[check.status] : <MinusCircle className="h-4 w-4 text-slate-300 mx-auto" />}
                            {check?.detailsUrl && (
                              <a
                                href={check.detailsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-slate-400 hover:text-slate-600"
                                title="View this run on GitHub"
                              >
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            )}
                            {failed && check && (
                              <button
                                onClick={() => createGuardrailFrom([toCiFailure(r.repo, check)])}
                                className="text-purple-600 hover:text-purple-700"
                                title={`Create a check from this failure (${check.output?.summary || check.name})`}
                              >
                                <Plus className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
